import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { Column, ColumnKeys, ComplianceAppWebComponentSchema } from '../types';
import {
  WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS,
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT,
} from 'apps/webcomponents/src/webcomponents.constants';
import { GenericGridSmartViewsWebComponent } from 'apps/webcomponents/src/grid/genericGridSmartViews';
import { GridViewWebComponent, GzPushBodyLayoutWebComponent, GzSideMenuWebComponent } from 'apps/webcomponents/src';
import {
  getSavedSmartViewsCategory,
  setSmartViewsPanelSizeBasedOnState,
} from 'apps/webcomponents/src/grid/genericGridSmartViews/utils';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { ElementClass } from 'apps/webcomponents/src/utils/htmlElement.utils';
import { convertGenericViewConfigToGridViewConfig } from 'apps/webcomponents/src/grid/genericGridSmartViews/types';
import {
  changeEmptyStateIcon,
  getCompanyFilterUiConfig,
  processExternalFilters,
} from 'apps/webcomponents/src/riskManagement/common/utils';
import { disclaimerLink, getComplianceLearnMoreButton, setGridCommonProperties } from '../../common/util';
import { ComplianceAppGridDataProvider } from '../dataProvider/ComplianceAppGridDataProvider';
import { ColumnDetails, ColumnType } from 'apps/webcomponents/src/grid/types/common';
import {
  compliantCountRenderer,
  controlIDRenderer,
  controlRenderer,
  ignoredCountRenderer,
  nonCompliantCountRenderer,
  scoringRenderer,
  sectionIDRenderer,
  sectionRenderer,
} from './CustomGridRenderers';
import { DefaultGridColumnFixedWidths } from '@nirvana/shared/dtos/types/shared/grid';
import { ControlStatus, ComplianceStandard } from '@nirvana/shared/dtos/types/compliance-reports/gz-console';
import { BaseWebComponentTranslator } from '../../../framework/BaseWebComponentTranslator';
import { ComplianceEvents } from '../../common/types';
import {
  complianceStandardOptions,
  controlComplianceScoreMap,
  complianceStandardFilterUiConfig,
  addDisclaimer,
} from '../../common/util';
import { formatLargeNumber } from 'apps/webcomponents/src/utils/formatter.utils';
import { TemplateState } from 'apps/webcomponents/src/types/common/types';
import { ImageState } from 'apps/webcomponents/src/ui/gzImageRepo/types';
import { ComplianceDisclaimer, LicenseLink, LicenseMessageFn, RiskMessageFn } from '../../common/constant';
import { getColumnKeyForFilter, getFilterWithDefaultValues } from 'apps/webcomponents/src/grid/utils/filters';
import { Filter } from 'apps/webcomponents/src/grid/gridFilters/types';

export class ComplianceAppView extends BaseWebComponentView {
  compliancePremiumUsable: boolean = false;

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$title = this.shadow.querySelector('gz-push-body-layout .title');
    this.dom.$loader = this.shadow.querySelector('gz-push-body-layout .loader');
    this.dom.$pageTitle = this.shadow.querySelector('.compliance-header .title');
    this.dom.$pushBodyLayout = this.shadow.querySelector(
      WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT
    ) as GzPushBodyLayoutWebComponent;

    this.dom.$grid = this.shadow.querySelector(
      `gz-push-body-layout ${WEB_COMPONENT_GRID_VIEW}`
    ) as GridViewWebComponent<Column, ColumnKeys>;

    this.dom.$loadingErrorMessage = this.shadow.querySelector('gz-push-body-layout .loading-error--message');
    this.dom.$gridSmartViews = this.shadow.querySelector(
      `gz-push-body-layout ${WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS}`
    ) as GenericGridSmartViewsWebComponent;
    this.dom.$smartViewsTitle = this.shadow.querySelector('gz-push-body-layout #smartViewsTitle');
    this.dom.$sideMenu = this.shadow.querySelector('gz-push-body-layout #sideMenu') as GzSideMenuWebComponent;
    // Risk management
    this.dom.$riskManagementAdditionalText = this.shadow.querySelector('#riskManagementAdditionalText');
    this.dom.$riskManagementAdditionalSubText = this.shadow.querySelector('#riskManagementAdditionalSubText');
    this.dom.$riskManagementAdditionalText.innerText = RiskMessageFn.NO_RISK_IDENTIFIED();
    this.dom.$riskManagementAdditionalSubText.innerText = RiskMessageFn.GO_TO_FINDING_PAGE();
    // this.dom.$disclaimer = {
    //   $text: this.shadow.querySelector('#disclaimer-info-box-text'),
    // };
    this.dom.$licenseDisclaimer = {
      $text: this.shadow.querySelector('#license-disclaimer-info-box-text'),
    };

    this.setGridActions();
    this.setInfoBox();
    this.setTranslatedStrings();
    this.setComplianceLearnMoreButton();
  }

  setComplianceLearnMoreButton() {
    this.dom.$complianceLearnMoreButton = this.shadow.querySelector('#complianceLearnMoreButton');
    const { show, text, linkAction } = getComplianceLearnMoreButton();
    if (show) {
      this.dom.$complianceLearnMoreButton.innerText = translateFn(text);
      this.dom.$complianceLearnMoreButton.onclick = () => linkAction();
    } else {
      this.setElementClass(this.dom.$complianceLearnMoreButton, ElementClass.HIDDEN);
    }
  }

  async buildSuccessView(data: ComplianceAppWebComponentSchema) {
    this.buildSmartViews().then(async () => {
      if (data.externalFilters?.length > 0) {
        await this.dom.$gridSmartViews.controller.setDefaultViewAsActiveView();
      }

      const activeView = this.dom.$gridSmartViews.controller.getActiveView();
      setSmartViewsPanelSizeBasedOnState(this.dom.$pushBodyLayout, this.dom.$gridSmartViews.smartViewState);
      await this.buildGrid(convertGenericViewConfigToGridViewConfig(activeView), data.externalFilters ?? []);
    });
  }

  private async buildSmartViews() {
    const smartViewDependencies = this.dependencies.gzDependencies;
    smartViewDependencies.componentName = 'compliance-grid';
    if (!(this.dom.$gridSmartViews instanceof GenericGridSmartViewsWebComponent)) {
      throw new Error('Missing grid smart views');
    }
    this.dom.$gridSmartViews.categoriesConfig = getSavedSmartViewsCategory(
      smartViewDependencies.componentName,
      this.getTranslations().defaultViewsTranslations,
      smartViewDependencies.api.clients.SmartViews.validateViewName
    );
    this.dom.$gridSmartViews.hasSearchInput = true;
    this.dom.$gridSmartViews.injectDependencies(smartViewDependencies);

    await this.dom.$gridSmartViews.start();
    this.removeElementClass(this.dom.$gridSmartViews, ElementClass.HIDDEN);
    this.setElementClass(this.dom.$loader, ElementClass.HIDDEN);
  }

  async buildGrid(view, externalFilters: Array<any> = []) {
    const {
      hasExternalFilters,
      initialViewWithoutExternalFilters,
      view: processedView,
    } = processExternalFilters(view, externalFilters);

    this.dom.$grid.propertiesSet = false;

    setGridCommonProperties(this.dom.$grid, processedView, hasExternalFilters, initialViewWithoutExternalFilters, this);

    this.dom.$grid.dataProvider = new ComplianceAppGridDataProvider(
      this.dependencies,
      this.dispatchGridEvent.bind(this)
    );
    this.dom.$grid.allColumnDetails = this.getGridColumns();
    this.dom.$grid.columnRenderers = {
      [ColumnKeys.CONTROL]: controlRenderer.bind(this),
      [ColumnKeys.CONTROL_ID]: controlIDRenderer.bind(this),
      [ColumnKeys.SECTION]: sectionRenderer.bind(this),
      [ColumnKeys.SECTION_ID]: sectionIDRenderer.bind(this),
      [ColumnKeys.CONTROL_COMPLIANCE_SCORE]: scoringRenderer.bind(this.dom, this),
      [ColumnKeys.COMPLIANT_COUNT]: compliantCountRenderer.bind(this.dom, this),
      [ColumnKeys.NON_COMPLIANT_COUNT]: nonCompliantCountRenderer.bind(this.dom, this),
      [ColumnKeys.IGNORED_COUNT]: ignoredCountRenderer.bind(this.dom, this),
    };
    this.dom.$grid.filtersUiConfigs = this.getFiltersUiConfig();
    this.dom.$grid.sideMenuParent = this.dom.$wrapper;
    this.dom.$grid.predefinedFiltersKeys = this.getPredefinedFiltersKeys();
    this.dom.$grid.enableRowClickEvent = true;
    this.dom.$grid.hasSelectableRows = false;
    this.dom.$grid.enableCSVExport = false;

    this.dom.$grid.propertiesSet = true;

    this.dom.$sideMenu.closeSideMenu();

    if (!this.dom.$grid.started) {
      this.dom.$grid.injectDependencies(this.dependencies.gzDependencies);
      await this.dom.$grid.start();
    }

    changeEmptyStateIcon(this.dom.$grid);

    // Show global action button in header once entire grid is built.
    this.removeElementClass(this.dom.$gridHeaderActions, ElementClass.HIDDEN);
    this.removeElementClass(this.dom.$gridInfoBox, ElementClass.HIDDEN);

    this.dom.$filterScore = this.dom.$grid.controller.view.dom.$gridFilters.controller.view.shadow.querySelector(
      `gz-enum-filter[columnkey="${ColumnKeys.CONTROL_COMPLIANCE_SCORE}"]`
    );
  }

  getTranslations() {
    return {
      smartViewsStrings: this.stringsTranslator.getSmartViewsTranslations(),
      defaultViewsTranslations: {
        compliance_posture: translateFn('Compliance posture'),
      },
    };
  }

  setTranslatedStrings(): void {
    this.dom.$pageTitle.innerHTML = this.dependencies.translateFn('Compliance manager');
    // this.dom.$disclaimer.$text.innerText = translateFn(
    //   'Currently, the Compliance feature supports evaluating up to {resourcesCount} resources', {
    //     resourcesCount: formatLargeNumber(ENDPOINT_COUNT_HARD_LIMIT)
    //   }
    // );
  }

  private getPredefinedFiltersKeys(): Array<ColumnKeys> {
    if (!Application.Main.company.isCustomer()) {
      return [ColumnKeys.COMPANY, ColumnKeys.COMPLIANCE_STANDARD, ColumnKeys.CONTROL_COMPLIANCE_SCORE];
    }

    return [ColumnKeys.COMPLIANCE_STANDARD, ColumnKeys.CONTROL_COMPLIANCE_SCORE];
  }

  private getFiltersUiConfig() {
    return [getCompanyFilterUiConfig(ColumnKeys.COMPANY), complianceStandardFilterUiConfig(this.dom.$grid)];
  }

  private setInfoBox() {
    // TODO(wchee): Hijacking the info-box slot for now. Remove this if this is ok.
    this.dom.$gridInfoBox = this.shadow.querySelector('#compliance-info-box');
    this.dom.complianceOverview = {
      $content: this.shadow.querySelector('#compliance-overview'),
    };
  }

  private setGridActions() {
    this.dom.$gridHeaderActions = this.shadow.querySelector('[slot=grid-header-actions-slot]');

    // Download report button configuration
    this.dom.downloadReport = {
      $button: this.shadow.querySelector('#download-report-button'),
      $tooltip: this.shadow.querySelector('#download-report-button-tooltip'),
    };
    this.dom.downloadReport.$button.innerText = translateFn('Download report');
    this.dom.downloadReport.$button.onclick = () =>
      this.dom.$grid.dispatchEvent(new CustomEvent(ComplianceEvents.DOWNLOAD_REPORT));
  }

  protected getGridColumns(): Array<ColumnDetails<Column, ColumnKeys>> {
    const columns: Array<ColumnDetails<Column, ColumnKeys>> = [
      {
        column: Column.COMPLIANCE_STANDARD,
        key: ColumnKeys.COMPLIANCE_STANDARD,
        columnName: translateFn('Compliance standard'),
        type: ColumnType.COMPLIANCE_SELECT,
        values: complianceStandardOptions,
        width: DefaultGridColumnFixedWidths.M,
        removable: false,
        sortable: false,
        isHiddenInGridSettings: true,
        filterResetValue: {
          value: ComplianceStandard.BDCHBWINDOWS,
        },
      },
      {
        column: Column.CONTROL_ID,
        key: ColumnKeys.CONTROL_ID,
        columnName: translateFn('Control ID'),
        type: ColumnType.NUMBER,
        width: DefaultGridColumnFixedWidths.S,
        removable: false,
        sortable: false,
        filteringDisabled: true,
      },
      {
        column: Column.CONTROL,
        key: ColumnKeys.CONTROL,
        columnName: translateFn('Control name'),
        type: ColumnType.STRING,
        width: DefaultGridColumnFixedWidths.L,
        removable: false,
        sortable: false,
        filteringDisabled: true,
      },
      // {
      //   column: Column.CATEGORY,
      //   key: ColumnKeys.CATEGORY,
      //   columnName: translateFn('Category'),
      //   type: ColumnType.STRING,
      //   width: DefaultGridColumnFixedWidths.S,
      //   removable: true,
      //   sortable: false,
      //   filteringDisabled: true,
      // },
      {
        column: Column.SECTION_ID,
        key: ColumnKeys.SECTION_ID,
        columnName: translateFn('Section ID'),
        type: ColumnType.STRING,
        width: DefaultGridColumnFixedWidths.S,
        removable: true,
        sortable: false,
        filteringDisabled: true,
      },
      {
        column: Column.SECTION,
        key: ColumnKeys.SECTION,
        columnName: translateFn('Section name'),
        type: ColumnType.STRING,
        width: DefaultGridColumnFixedWidths.L,
        removable: true,
        sortable: false,
        filteringDisabled: true,
      },
      {
        column: Column.CONTROL_COMPLIANCE_SCORE,
        key: ColumnKeys.CONTROL_COMPLIANCE_SCORE,
        columnName: translateFn('Score'),
        type: ColumnType.EDITABLE_SELECT,
        values: this.getControlComplianceScoreColumnValues(),
        width: DefaultGridColumnFixedWidths.L,
        removable: false,
        sortable: false,
      },
      {
        column: Column.COMPLIANT_COUNT,
        key: ColumnKeys.COMPLIANT_COUNT,
        columnName: translateFn('Compliant'),
        type: ColumnType.NUMBER,
        width: DefaultGridColumnFixedWidths.S,
        removable: true,
        sortable: false,
        filteringDisabled: true,
      },
      {
        column: Column.NON_COMPLIANT_COUNT,
        key: ColumnKeys.NON_COMPLIANT_COUNT,
        columnName: translateFn('Non-compliant'),
        type: ColumnType.NUMBER,
        width: DefaultGridColumnFixedWidths.S,
        removable: true,
        sortable: false,
        filteringDisabled: true,
      },
      {
        column: Column.IGNORED_COUNT,
        key: ColumnKeys.IGNORED_COUNT,
        columnName: translateFn('Ignored'),
        type: ColumnType.NUMBER,
        width: DefaultGridColumnFixedWidths.S,
        removable: true,
        sortable: false,
        filteringDisabled: true,
      },
    ];

    if (Application.Main.company.isPartner()) {
      columns.unshift({
        column: Column.COMPANY,
        key: ColumnKeys.COMPANY,
        columnName: translateFn('Company'),
        type: ColumnType.REMOTE_SELECT,
        removable: false,
        sortable: false,
        isHiddenInGridSettings: true,
        filterResetValue: {
          exactMatch: Application.Main.getUserCompany()?.id,
        },
      });
    }

    return columns;
  }

  setDefaultState(): void {
    this.dom.complianceOverview.$content.savedSmartView = false;
    this.dom.complianceOverview.$content.hasAccessToComplianceStandard = false;
    this.dom.$grid.controller.view.dom.$discardViewChanges.disabled = false;
    this.dom.$grid.controller.view.dom.$favoriteIcon.disabled = false;
    this.dom.$grid.controller.view.dom.$saveViewAsButton.disabled = false;
    this.dom.$grid.controller.view.dom.$gridSettingsMenuTrigger.disabled = false;
    this.dom.downloadReport.$button.disabled = true;
    this.compliancePremiumUsable = false;
    this.dom.$grid.controller.view.dom.$gzImageRepo.state = ImageState.EMPTY;
    this.dom.$grid.controller.updateNoResultsMessage(RiskMessageFn.NO_RESULTS());
    this.dom.$riskManagementAdditionalText.innerText = RiskMessageFn.NO_RESULTS_ADJUST_FILTER();
    this.dom.$riskManagementAdditionalSubText.innerText = '';
    this.dom.$riskManagementAdditionalText.style.maxWidth = '300px';
    this.setElementClass(this.dom.$licenseDisclaimer.$text, ElementClass.HIDDEN);
    this.setElementClass(this.dom.$complianceLearnMoreButton, ElementClass.HIDDEN);
  }

  setDisableSmartViewAction(value: boolean): void {
    this.dom.$grid.controller.view.dom.$saveViewButton.disabled = value;
    this.dom.$grid.controller.view.dom.$discardViewChanges.disabled = value;
    this.dom.$grid.controller.view.dom.$favoriteIcon.disabled = value;
    this.dom.$grid.controller.view.dom.$saveViewAsButton.disabled = value;
    this.dom.$grid.controller.view.dom.$gridSettingsMenuTrigger.disabled = value;
  }

  setEmptyRowsWithOverallChecks(): void {
    this.dom.$grid.controller.view.dom.$gzImageRepo.state = ImageState.EMPTY;
    this.dom.$grid.controller.updateNoResultsMessage(RiskMessageFn.NO_RESULTS());
    this.dom.$riskManagementAdditionalText.innerText = RiskMessageFn.NO_RESULTS_ADJUST_FILTER();
    this.dom.$riskManagementAdditionalSubText.innerText = '';
    this.dom.$riskManagementAdditionalText.style.maxWidth = '300px';
    this.dom.$grid.controller.view.resetTemplateState(this.dom.$grid.controller.view.dom.$wrapper);
    this.dom.$grid.controller.view.setTemplateState(this.dom.$grid.controller.view.dom.$wrapper, TemplateState.EMPTY);
  }

  setNoFindingsView(): void {
    this.dom.$grid.controller.view.dom.$gzImageRepo.state = ImageState.START;
    this.dom.$grid.controller.updateNoResultsMessage(RiskMessageFn.START_SCAN());
    this.dom.$riskManagementAdditionalText.innerText = RiskMessageFn.NO_RISK_IDENTIFIED();
    this.dom.$riskManagementAdditionalSubText.innerText = RiskMessageFn.GO_TO_FINDING_PAGE();
    this.dom.$grid.controller.view.resetTemplateState(this.dom.$grid.controller.view.dom.$wrapper);
    this.dom.$grid.controller.view.setTemplateState(this.dom.$grid.controller.view.dom.$wrapper, TemplateState.EMPTY);
    this.setElementClass(this.dom.$complianceLearnMoreButton, ElementClass.HIDDEN);
  }

  setNoLicenseKeyDetectedView(showComplianceLearnMoreButton: boolean = true): void {
    this.dom.complianceOverview.$content.savedSmartView = true;
    this.dom.$grid.controller.view.dom.$grid.items = [];
    this.dom.$grid.controller.view.resetTemplateState(this.dom.$grid.controller.view.dom.$wrapper);
    this.dom.$grid.controller.view.setTemplateState(this.dom.$grid.controller.view.dom.$wrapper, TemplateState.EMPTY);
    this.dom.$grid.controller.view.dom.$gzImageRepo.state = ImageState.DENIED;
    this.dom.$grid.controller.updateNoResultsMessage(LicenseMessageFn.LICENSE_RESTRICTED_TITLE());
    this.dom.$riskManagementAdditionalText.innerText = LicenseMessageFn.LICENSE_RESTRICTED_MESSAGE();
    this.dom.$riskManagementAdditionalSubText.innerText = '';

    showComplianceLearnMoreButton
      ? getComplianceLearnMoreButton().show &&
        this.removeElementClass(this.dom.$complianceLearnMoreButton, ElementClass.HIDDEN)
      : this.setElementClass(this.dom.$complianceLearnMoreButton, ElementClass.HIDDEN);
  }

  setDisclaimer(disclaimer: ComplianceDisclaimer): void {
    const { text: complianceLearnMoreText, link: complianceLearnMoreLink, show } = getComplianceLearnMoreButton();
    const link = show ? disclaimerLink(complianceLearnMoreLink, complianceLearnMoreText) : '';

    switch (disclaimer) {
      case ComplianceDisclaimer.PURCHASE_ADD_ON:
        addDisclaimer(this.dom.$licenseDisclaimer.$text, LicenseMessageFn.LICENSE_UPGRADE_DISCLAIMER(link));
        break;
      case ComplianceDisclaimer.FREE_TIER_LIMIT:
        addDisclaimer(this.dom.$licenseDisclaimer.$text, LicenseMessageFn.LICENSE_FREE_TIER_LIMIT(link));
        break;
      case ComplianceDisclaimer.HARD_LIMIT:
        addDisclaimer(
          this.dom.$licenseDisclaimer.$text,
          LicenseMessageFn.LICENSE_HARD_LIMIT(
            disclaimerLink(LicenseLink.CONTACT_SUPPORT_LINK, LicenseMessageFn.CONTACT_SUPPORT())
          )
        );
        break;
      default:
        throw new Error('Invalid disclaimer type');
    }
  }

  setDisableFilterScore(hasAccessToComplianceStandard: boolean) {
    const disable = !hasAccessToComplianceStandard;
    this.dom.$filterScore.disabled = disable;

    if (disable) {
      this.dom.$grid.filters = this.dom.$grid.filters.map(filter => {
        if (filter.hasOwnProperty(ColumnKeys.CONTROL_COMPLIANCE_SCORE)) {
          return {
            [ColumnKeys.CONTROL_COMPLIANCE_SCORE]: {
              in: this.getControlComplianceScoreColumnValues().map(score => score.value),
            },
          };
        } else {
          return filter;
        }
      });
      const columnDetailsByKey = Object.fromEntries(
        this.dom.$grid.allColumnDetails.map(columnDetail => [columnDetail.key, columnDetail])
      );

      const filterWithDefaultScore: Array<Filter<ColumnKeys>> = [];
      for (const filter of this.dom.$grid.filters || []) {
        const columnKey = getColumnKeyForFilter<ColumnKeys>(filter);
        if (columnKey === ColumnKeys.CONTROL_COMPLIANCE_SCORE) {
          const columnDetails = columnDetailsByKey[columnKey];
          filterWithDefaultScore.push(getFilterWithDefaultValues(columnDetails));
        } else {
          filterWithDefaultScore.push(filter);
        }
      }

      this.dom.$grid.controller.view.dom.$gridFilters.controller.updateFilters(filterWithDefaultScore);
    }
  }

  setComplianceOverview(data: {
    overallCompliance: number;
    compliantCount: number;
    nonCompliantCount: number;
    ignoredCount: number;
    totalChecks: number;
  }): void {
    this.dom.complianceOverview.$content.payload = {
      overallCompliance: data.overallCompliance,
      compliantCount: data.compliantCount,
      nonCompliantCount: data.nonCompliantCount,
      ignoredCount: data.ignoredCount,
      totalChecks: data.totalChecks,
    };

    this.dom.complianceOverview.$content.loading = false;
  }

  private getControlComplianceScoreColumnValues() {
    // TODO: to be changed to a RangeFilter
    return [
      ...Object.values(controlComplianceScoreMap)
        .sort((a, b) => b.queryFilter.min - a.queryFilter.min)
        .map(x => ({
          ...x.columnFilter,
          label: `${x.columnFilter.label} ${translateFn('compliant')}`,
        })),
      { label: BaseWebComponentTranslator.getInstance().getControlStatus(ControlStatus.NOT_EVALUATED), value: 'NA' },
    ];
  }

  private dispatchGridEvent(event, detail) {
    this.dom.$grid.dispatchEvent(new CustomEvent(event, { detail: detail }));
  }
}
